import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { fetchLmsResourceFileBlob, lmsAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import { useSupervisorTeacherContext } from '../../contexts/SupervisorTeacherContext';
import {
  Alert,
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  LinearProgress,
  Grid,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import DownloadIcon from '@mui/icons-material/Download';
import PageBackButton from '../../components/PageBackButton';

// Helper function to extract YouTube video ID from URL
const getYouTubeVideoId = (url) => {
  if (!url) return null;
  
  // Handle various YouTube URL formats
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^&\n?#]+)/,
    /^([a-zA-Z0-9_-]{11})$/ // Direct video ID
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  return null;
};

const CONTENT_TYPE_LABELS = {
  teacher_training: 'My Training Content',
  student_teaching: 'Class Content',
};

const MyTraining = ({ contentType = 'teacher_training' }) => {
  const queryClient = useQueryClient();
  const picker = useSupervisorTeacherContext();
  const teacherIdParam = picker.isSupervisorPicker ? picker.selectedTeacherId : undefined;
  const readOnlyView = picker.isSupervisorPicker && !picker.viewingOwnSchedule;
  const contentParams = useMemo(() => {
    const params = { content_type: contentType };
    if (teacherIdParam) params.teacher_id = teacherIdParam;
    return params;
  }, [contentType, teacherIdParam]);

  const { data, isLoading } = useQuery(
    ['lms-my-content', contentType, teacherIdParam],
    () => lmsAPI.getMyContent(contentParams).then((res) => res.data),
    { enabled: picker.ready }
  );
  const { data: progressData } = useQuery(
    teacherIdParam
      ? ['lms-progress', teacherIdParam]
      : ['lms-my-progress'],
    () => (
      teacherIdParam
        ? lmsAPI.getProgress(teacherIdParam)
        : lmsAPI.getMyProgress()
    ).then((res) => res.data),
    {
      enabled: picker.ready && !!data?.data && contentType === 'teacher_training',
    }
  );
  
  const [videoPlayerOpen, setVideoPlayerOpen] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerUrl, setViewerUrl] = useState('');
  const [viewerTitle, setViewerTitle] = useState('');
  const [viewerIsImage, setViewerIsImage] = useState(false);

  const closeViewer = () => {
    setViewerOpen(false);
    setViewerIsImage(false);
    setViewerUrl((prev) => {
      if (prev && String(prev).startsWith('blob:')) URL.revokeObjectURL(prev);
      return '';
    });
  };

  const progressByResource = useMemo(() => {
    const map = {};
    (progressData?.data || []).forEach((p) => {
      const id = p.resource_id || p.video_id;
      map[id] = { is_opened: !!p.is_opened, is_completed: !!p.is_completed };
    });
    return map;
  }, [progressData?.data]);

  const progressMutation = useMutation((payload) => lmsAPI.updateProgress(payload), {
    onSuccess: () => {
      queryClient.invalidateQueries(['lms-my-content']);
      queryClient.invalidateQueries(['lms-progress']);
      queryClient.invalidateQueries(['lms-my-progress']);
    },
  });

  const trackProgress = (resourceId, isOpened, isCompleted) => {
    if (readOnlyView || contentType !== 'teacher_training') return;
    progressMutation.mutate({
      resource_id: resourceId,
      video_id: resourceId,
      is_opened: isOpened,
      is_completed: isCompleted,
    });
  };

  const openResource = async (resource, { recordProgress = true } = {}) => {
    const resourceId = resource.id;
    const type = resource.resource_type || 'video';
    const contentUrl = resource.content_url || resource.file_url || resource.youtube_url || resource.source_url;

    if (type === 'video') {
      setCurrentVideoUrl(contentUrl);
      setVideoPlayerOpen(true);
      if (recordProgress) trackProgress(resourceId, true, false);
      return;
    }

    if (!resourceId) return;
    try {
      const blob = await fetchLmsResourceFileBlob(resourceId);
      const url = URL.createObjectURL(blob);
      setViewerUrl((prev) => {
        if (prev && String(prev).startsWith('blob:')) URL.revokeObjectURL(prev);
        return url;
      });
      setViewerIsImage(String(blob.type || '').startsWith('image/'));
      setViewerTitle(resource.title);
      setViewerOpen(true);
      if (recordProgress) trackProgress(resourceId, true, false);
    } catch (error) {
      toast.error(error.message || 'Could not open file');
    }
  };

  // Calculate progress statistics (based on completion, not evaluation)
  const progressStats = useMemo(() => {
    if (!data?.data) return { total: 0, completed: 0, opened: 0, percentage: 0 };
    
    const assignments = data.data;
    const progressList = progressData?.data || [];
    const completedSet = new Set(
      progressList.filter((p) => p.is_completed).map((p) => p.resource_id || p.video_id)
    );
    const openedSet = new Set(
      progressList.filter((p) => p.is_opened).map((p) => p.resource_id || p.video_id)
    );

    let totalResources = 0;
    let completedResources = 0;
    let openedResources = 0;

    const collectResourceIds = (item) => {
      const ids = [];
      const list = item.resources || item.videos || [];
      if (list.length) {
        list.forEach((r) => ids.push(r.id));
      } else if (
        (item.assignment_type === 'video' || item.assignment_type === 'resource') &&
        (item.resource_id || item.video_id || item.id)
      ) {
        ids.push(item.resource_id || item.video_id || item.id);
      }
      return ids;
    };

    assignments.forEach((item) => {
      const ids = collectResourceIds(item);
      ids.forEach((rid) => {
        totalResources++;
        if (completedSet.has(rid)) completedResources++;
        if (openedSet.has(rid)) openedResources++;
      });
    });

    const percentage = totalResources > 0 ? Math.round((completedResources / totalResources) * 100) : 0;

    return {
      total: totalResources,
      completed: completedResources,
      opened: openedResources,
      percentage,
    };
  }, [data?.data, progressData?.data]);

  const pageTitle = CONTENT_TYPE_LABELS[contentType] || CONTENT_TYPE_LABELS.teacher_training;
  const viewingTeacherName = data?.viewingTeacherName || picker.selectedTeacherName || '';

  if (picker.isSupervisorPicker && picker.teachersLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (picker.isSupervisorPicker && !picker.ready) {
    return (
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <PageBackButton />
          <Typography variant="h4">{pageTitle}</Typography>
        </Stack>
        <Alert severity="info">Select a teacher from the header dropdown to view their LMS content.</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
        <PageBackButton />
        <Typography variant="h4">{pageTitle}</Typography>
      </Stack>

      {picker.isSupervisorPicker && viewingTeacherName ? (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'info.50', borderLeft: '4px solid', borderColor: 'info.main' }}>
          <Typography variant="subtitle1" fontWeight={700}>
            Viewing LMS for: {viewingTeacherName}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {readOnlyView
              ? 'Read-only view of this teacher\u2019s assigned content and progress. Use the header dropdown to switch teachers.'
              : 'Your own training content. You can complete resources from here.'}
          </Typography>
        </Paper>
      ) : null}

      {/* Progress Dashboard (only for My Training / teacher_training) */}
      {contentType === 'teacher_training' && !isLoading && data?.data && data.data.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Learning Progress
          </Typography>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Overall Completion
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {progressStats.percentage}%
                  </Typography>
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={progressStats.percentage} 
                  sx={{ height: 10, borderRadius: 5 }}
                  color={progressStats.percentage === 100 ? 'success' : 'primary'}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="h4" color="primary" fontWeight="bold">
                        {progressStats.total}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Total Resources
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card variant="outlined">
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Typography variant="h4" color="success.main" fontWeight="bold">
                        {progressStats.completed}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Completed
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Paper>
      )}

      <Paper>
        {isLoading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : !data?.data || data.data.length === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography>No assignments found</Typography>
          </Box>
        ) : (
          <Grid container spacing={2} sx={{ p: 2 }}>
            {data.data.map((item) => {
              const renderResourceList = (resourcesToShow) => {
                if (!resourcesToShow?.length) return null;
                const resourceCard = (resource) => {
                  const resourceId = resource.id;
                  const progress = progressByResource[resourceId] || {};
                  const isOpened = progress.is_opened;
                  const isCompleted = progress.is_completed;
                  const type = resource.resource_type || 'video';
                  const contentUrl = resource.content_url || resource.file_url || resource.youtube_url || resource.source_url;
                  const statusLabel = isCompleted ? 'Completed' : isOpened ? 'Opened' : null;
                  const actionLabel =
                    type === 'video'
                      ? 'Watch Video'
                      : type === 'worksheet'
                        ? 'Download'
                        : 'Open';
                  const actionIcon =
                    type === 'video' ? <PlayArrowIcon /> : type === 'worksheet' ? <DownloadIcon /> : type === 'pdf' ? <PictureAsPdfIcon /> : <ImageIcon />;

                  return (
                    <Grid item key={resourceId} xs={12} sm={6} md={4}>
                      <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                          <Typography variant="subtitle2" fontWeight={600}>
                            {resource.title}
                          </Typography>
                          <Chip label={type} size="small" />
                        </Stack>
                        {resource.month_name && (
                          <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
                            {resource.month_name}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" gap={1}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={actionIcon}
                            onClick={() => openResource(resource, { recordProgress: !readOnlyView })}
                            disabled={type === 'video' ? !getYouTubeVideoId(contentUrl) : !resourceId}
                          >
                            {actionLabel}
                          </Button>
                          {contentType === 'teacher_training' && (
                            <>
                              {!readOnlyView && (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  startIcon={<CheckCircleIcon />}
                                  onClick={() => trackProgress(resourceId, true, true)}
                                  disabled={isCompleted}
                                >
                                  Mark Complete
                                </Button>
                              )}
                              {statusLabel && (
                                <Chip
                                  icon={isCompleted ? <CheckCircleIcon /> : <PlayArrowIcon />}
                                  label={statusLabel}
                                  color={isCompleted ? 'success' : 'primary'}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </>
                          )}
                        </Stack>
                      </Paper>
                    </Grid>
                  );
                };
                return (
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    {resourcesToShow.map(resourceCard)}
                  </Grid>
                );
              };

              const resourcesToShow =
                item.resources?.length > 0
                  ? item.resources
                  : item.videos?.length > 0
                    ? item.videos
                    : (item.assignment_type === 'video' || item.assignment_type === 'resource') &&
                        (item.resource_id || item.video_id || item.video_url)
                      ? [{
                          id: item.resource_id || item.video_id || item.id,
                          title: item.video_title,
                          resource_type: 'video',
                          youtube_url: item.video_url || item.youtube_url,
                          content_url: item.video_url || item.youtube_url,
                        }]
                      : [];

              if (item.assignment_type === 'module') {
                const activitiesMap = {};
                (item.resources || item.videos || []).forEach((r) => {
                  const key = r.month_id || r.month_name || 'unknown';
                  if (!activitiesMap[key]) {
                    activitiesMap[key] = { month_id: r.month_id, month_name: r.month_name || 'Activity', resources: [] };
                  }
                  activitiesMap[key].resources.push(r);
                });
                const activities = Object.values(activitiesMap).filter((act) => act.resources.length > 0);

                return (
                  <Grid item key={item.id} xs={12}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Typography fontWeight={600}>{item.module_name || item.assignment_breadcrumb?.module_name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {item.assignment_breadcrumb?.program_name} → {item.assignment_breadcrumb?.course_name} → {item.assignment_breadcrumb?.module_name}
                          </Typography>
                          <Chip label="module" size="small" sx={{ mt: 1, width: 'fit-content' }} />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          {activities.map((act, idx) => (
                            <Grid item key={act.month_id || act.month_name + idx} xs={12} sm={6} md={4}>
                              <Accordion>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                    <Typography fontWeight={600}>{act.month_name}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {act.resources.length} resource(s)
                                    </Typography>
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                  {renderResourceList(act.resources)}
                                </AccordionDetails>
                              </Accordion>
                            </Grid>
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                );
              }

              const mainTitle =
                item.assignment_type === 'video' || item.assignment_type === 'resource'
                  ? (item.video_title || item.assignment_breadcrumb?.video_title)
                  : (item.month_name || item.assignment_breadcrumb?.month_name);
              return (
                <Grid item key={item.id} xs={12} sm={6} md={4}>
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography fontWeight={600}>{mainTitle}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {item.assignment_breadcrumb?.program_name} → {item.assignment_breadcrumb?.course_name} → {item.assignment_breadcrumb?.module_name}
                          {item.assignment_breadcrumb?.month_name ? ` → ${item.assignment_breadcrumb?.month_name}` : ''}
                          {item.assignment_type === 'video' && item.assignment_breadcrumb?.video_title ? ` → ${item.assignment_breadcrumb.video_title}` : ''}
                        </Typography>
                        <Chip
                          label={item.assignment_type}
                          size="small"
                          sx={{ mt: 1, width: 'fit-content' }}
                          color={item.assignment_type === 'video' || item.assignment_type === 'resource' ? 'primary' : 'default'}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        {item.assignment_type === 'video' || item.assignment_type === 'resource'
                          ? `Resource: ${item.video_title}`
                          : `Activity: ${item.month_name}`}
                      </Typography>
                      {(item.assignment_breadcrumb?.resource_count || item.assignment_breadcrumb?.video_count) > 0 && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                          Contains {item.assignment_breadcrumb?.resource_count || item.assignment_breadcrumb?.video_count} resource(s)
                        </Typography>
                      )}
                      {renderResourceList(resourcesToShow)}
                    </AccordionDetails>
                  </Accordion>
                </Grid>
              );
            })}
          </Grid>
        )}

      </Paper>

      {/* Video Player Dialog */}
      <Dialog
        open={videoPlayerOpen}
        onClose={() => setVideoPlayerOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Video Player</Typography>
            <IconButton onClick={() => setVideoPlayerOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', backgroundColor: 'black' }}>
          {currentVideoUrl && getYouTubeVideoId(currentVideoUrl) ? (
            <Box
              component="iframe"
              src={`https://www.youtube.com/embed/${getYouTubeVideoId(currentVideoUrl)}?autoplay=1`}
              sx={{
                width: '100%',
                height: '100%',
                minHeight: '600px',
                border: 'none',
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                Invalid YouTube URL
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={viewerOpen} onClose={closeViewer} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{viewerTitle || 'Resource Viewer'}</Typography>
            <IconButton onClick={closeViewer} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, minHeight: 480 }}>
          {viewerUrl && viewerIsImage ? (
            <Box
              component="img"
              src={viewerUrl}
              alt={viewerTitle || 'File'}
              sx={{ display: 'block', maxWidth: '100%', mx: 'auto' }}
            />
          ) : viewerUrl ? (
            <Box
              component="iframe"
              src={viewerUrl}
              sx={{ width: '100%', minHeight: 640, border: 'none' }}
              title={viewerTitle}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default MyTraining;
